import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { FilterSelect, FilterSelectItem } from "@/components/ui/filter-select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  ClipboardCheck,
  Search,
  ChevronRight,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  X,
  SkipForward,
  ExternalLink,
  Info,
  DatabaseZap,
  ArrowRight,
  RotateCw,
  Check,
} from 'lucide-react';
import { runEndpoint } from '@/services/flow';
import { fetchData } from '@/fetchapi/fetchapi';
import { toast } from 'sonner';
import { Link, useSearchParams } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import {
  DataSource,
  DataElement,
  ElementVersion,
  ElementVersionProcessingRule,
  ProcessingRule,
  ReferenceList,
  ReferenceValueView,
  SourceFieldMappingView,
  MasterRecord,
} from '@/entities/all';
import FieldChangeHistory from '@/components/act-scores/FieldChangeHistory';

// --- Helper functions ---

const isSkippableIssue = (field) => {
  const reason = (field.accuracy_reason || field.message || '').toLowerCase();
  return reason.includes('empty') || reason.includes('junk') || reason.includes('placeholder');
};

const getIssueKey = (field) => `${field.accuracy_reason || field.message || ''}|||${field.rawValue || ''}`;

const getScoreColor = (score, max) => {
  const pct = (score / max) * 100;
  if (pct >= 75) return { bg: 'var(--pill-green-strong-bg)', color: 'var(--pill-green-strong-color)' };
  if (pct >= 50) return { bg: 'var(--pill-amber-bg)', color: 'var(--pill-amber-color)' };
  return { bg: 'var(--pill-red-bg)', color: 'var(--pill-red-color)' };
};

function ScorePill({ score, max, label, reason, greyed }) {
  const cfg = getScoreColor(score, max);
  const text = label ? `${label}:${score}` : `${score}/${max}`;
  const pill = (
    <span
      className="inline-flex items-center h-[18px] rounded-[9px] px-2 text-xs font-medium whitespace-nowrap"
      style={{ background: greyed ? 'var(--pill-neutral-bg)' : cfg.bg, color: greyed ? 'var(--pill-neutral-color)' : cfg.color, opacity: greyed ? 0.5 : 1 }}
    >
      {text}
    </span>
  );
  if (!reason) return pill;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild><span className="cursor-help">{pill}</span></TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs"><p className="text-xs">{reason}</p></TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// --- Main Component ---

export default function DataReviewWorkbench() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read URL params exactly once using lazy initializer pattern
  const [initialUrl] = useState(() => ({
    sourceId: searchParams.get('sourceId') ? parseInt(searchParams.get('sourceId')) : null,
    date: searchParams.get('date') || null,
    record: searchParams.get('record') || null,
    field: searchParams.get('field') || null,
  }));

  // Data source & date selection
  const [dataSources, setDataSources] = useState([]);
  const [selectedDataSourceId, setSelectedDataSourceId] = useState(initialUrl.sourceId);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDatePath, setSelectedDatePath] = useState(initialUrl.date);
  const [datesLoading, setDatesLoading] = useState(false);
  const [dateNotProcessed, setDateNotProcessed] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Work queue
  const [queueRecords, setQueueRecords] = useState([]);
  const [queueTotal, setQueueTotal] = useState(0);
  const [queuePage, setQueuePage] = useState(1);
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueSearch, setQueueSearch] = useState('');

  // ACT summary
  const [actSummary, setActSummary] = useState(null);

  // Record detail
  const [selectedRecordKey, setSelectedRecordKey] = useState(initialUrl.record);
  const [recordFields, setRecordFields] = useState(null);
  const [recordLoading, setRecordLoading] = useState(false);
  const [reviewerNotes, setReviewerNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Master record for selected record (single row, loaded per record)
  const [currentMasterRecord, setCurrentMasterRecord] = useState(null);

  // Field display mode: 'diffs' shows only fields differing from master, 'all' shows everything
  const [fieldDisplayMode, setFieldDisplayMode] = useState('diffs');

  // Field overrides: { [fieldName]: { editValue, validationResult, validating, accepted, editing } }
  const [fieldOverrides, setFieldOverrides] = useState({});

  // Reference list values for fields that have ref lists: { [fieldName]: string[] }
  const [refListValues, setRefListValues] = useState({});

  // Evidence panel
  const [selectedFieldName, setSelectedFieldName] = useState(initialUrl.field);
  const [elementInfo, setElementInfo] = useState(null);
  const [elementInfoLoading, setElementInfoLoading] = useState(false);

  // Master view panel (right side)
  const [showMasterView, setShowMasterView] = useState(false);
  const [selectedMasterField, setSelectedMasterField] = useState(null);

  // Keep refs in sync with state for use in async callbacks
  const selectedDataSourceIdRef = useRef(selectedDataSourceId);
  const selectedDatePathRef = useRef(selectedDatePath);
  const selectedRecordKeyRef = useRef(selectedRecordKey);
  useEffect(() => { selectedDataSourceIdRef.current = selectedDataSourceId; }, [selectedDataSourceId]);
  useEffect(() => { selectedDatePathRef.current = selectedDatePath; }, [selectedDatePath]);
  useEffect(() => { selectedRecordKeyRef.current = selectedRecordKey; }, [selectedRecordKey]);

  // Track previous values to detect user-driven changes vs mount
  const prevSourceId = useRef(selectedDataSourceId);
  const prevDatePath = useRef(selectedDatePath);
  const prevRecordKey = useRef(selectedRecordKey);

  // Sync state → URL params
  useEffect(() => {
    const params = {};
    if (selectedDataSourceId) params.sourceId = String(selectedDataSourceId);
    if (selectedDatePath) params.date = selectedDatePath;
    if (selectedRecordKey) params.record = selectedRecordKey;
    if (selectedFieldName) params.field = selectedFieldName;
    setSearchParams(params, { replace: true });
  }, [selectedDataSourceId, selectedDatePath, selectedRecordKey, selectedFieldName]);

  // Load data sources on mount
  useEffect(() => {
    loadDataSources();
  }, []);

  // Load dates when data source changes
  useEffect(() => {
    if (!selectedDataSourceId) return;
    const changed = prevSourceId.current !== null && prevSourceId.current !== selectedDataSourceId;
    prevSourceId.current = selectedDataSourceId;
    loadDateEntries(!changed);
    if (changed) {
      setSelectedDatePath(null);
      setSelectedRecordKey(null);
      setQueueRecords([]);
      setActSummary(null);
      setCurrentMasterRecord(null);
      setDateNotProcessed(false);
    }
  }, [selectedDataSourceId]);

  // Load queue + stats when date is set
  useEffect(() => {
    if (!selectedDataSourceId || !selectedDatePath) return;
    const changed = prevDatePath.current !== null && prevDatePath.current !== selectedDatePath;
    prevDatePath.current = selectedDatePath;
    loadWorkQueue();
    loadActSummary();
    if (changed) {
      setRecordFields(null);
      setCurrentMasterRecord(null);
      setQueuePage(1);
      // Keep selectedRecordKey and selectedFieldName so they persist across date changes
    }
  }, [selectedDataSourceId, selectedDatePath]);

  // Reload queue on page change
  useEffect(() => {
    if (selectedDataSourceId && selectedDatePath) {
      loadWorkQueue();
    }
  }, [queuePage]);

  // Load record details + master when record or date changes
  useEffect(() => {
    if (!selectedDataSourceId || !selectedDatePath || selectedRecordKey === null) return;
    const recordChanged = prevRecordKey.current !== null && prevRecordKey.current !== selectedRecordKey;
    prevRecordKey.current = selectedRecordKey;
    loadRecordDetails();
    loadCurrentMasterRecord();
    if (recordChanged) {
      setSelectedFieldName(null);
      setElementInfo(null);
      setFieldOverrides({});
      setFieldDisplayMode('diffs');
      setShowMasterView(false);
      setSelectedMasterField(null);
    }
  }, [selectedRecordKey, selectedDatePath]);

  // Open field evidence panel if URL had a field param
  useEffect(() => {
    if (selectedFieldName && recordFields && !elementInfo && !elementInfoLoading) {
      handleFieldClick(selectedFieldName);
    }
  }, [recordFields]);

  // --- Data loading ---

  const loadDataSources = async () => {
    try {
      const data = await DataSource.list();
      setDataSources(data || []);
      // Only auto-select if no source was set (neither from URL nor prior selection)
      if (!selectedDataSourceIdRef.current && data?.length) {
        const golden = data.find(ds => ds.is_golden_source);
        setSelectedDataSourceId((golden || data[0]).id);
      }
    } catch (error) {
      console.error('Error loading data sources:', error);
      toast.error('Failed to load data sources');
    }
  };

  const loadDateEntries = async (keepCurrentDate = false) => {
    setDatesLoading(true);
    setAvailableDates([]);
    try {
      const response = await runEndpoint('cortex.vdd.list_datasource_files', {
        dataSourceId: selectedDataSourceIdRef.current
      });
      const rawEntries = response?.files || response || [];
      setAvailableDates(rawEntries);
      if (rawEntries.length > 0) {
        const currentDate = selectedDatePathRef.current;
        const hasCurrentDate = keepCurrentDate && currentDate && rawEntries.some(e => e.datePath === currentDate);
        if (!hasCurrentDate) {
          setSelectedDatePath(rawEntries[0].datePath);
        }
      } else {
        setSelectedDatePath(null);
      }
    } catch (error) {
      console.error('Error loading date entries:', error);
      toast.error('Failed to load date entries');
    } finally {
      setDatesLoading(false);
    }
  };

  const loadWorkQueue = async () => {
    setQueueLoading(true);
    setDateNotProcessed(false);
    try {
      const response = await runEndpoint('cortex.get_records_with_scores', {
        dataSourceId: parseInt(selectedDataSourceId),
        datePath: selectedDatePath,
        page: queuePage,
        pageSize: 50,
        sortBy: 'record_total_act_score',
        sortDir: 'asc',
        filterBelowThreshold: false,
      });

      if (response?.error) {
        // Data file not found means this date hasn't been processed yet
        setDateNotProcessed(true);
        setQueueRecords([]);
        setQueueTotal(0);
      } else {
        setQueueRecords(response.records || []);
        setQueueTotal(response.total || 0);
      }
    } catch (error) {
      console.error('Error loading work queue:', error);
      toast.error('Failed to load work queue');
      setQueueRecords([]);
    } finally {
      setQueueLoading(false);
    }
  };

  const handleRunProcessing = async () => {
    const dateEntry = availableDates.find(d => d.datePath === selectedDatePath);
    if (!dateEntry?.files?.length) {
      toast.error('No files found for this date');
      return;
    }
    setProcessing(true);
    try {
      for (const file of dateEntry.files) {
        await fetchData('governance/process', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            dataSourceId: parseInt(selectedDataSourceId),
            sourceFile: file
          })
        });
      }
      toast.success('Processing started');
      setDateNotProcessed(false);
      loadWorkQueue();
      loadActSummary();
    } catch (error) {
      console.error('Error running processing:', error);
      toast.error('Processing failed: ' + (error.message || 'Unknown error'));
    } finally {
      setProcessing(false);
    }
  };

  const loadActSummary = async () => {
    try {
      const response = await runEndpoint('cortex.get_act_statistics', {
        dataSourceId: parseInt(selectedDataSourceId),
        datePath: selectedDatePath
      });
      if (!response?.error) {
        setActSummary(response?.summary || null);
      }
    } catch (error) {
      console.error('Error loading ACT summary:', error);
    }
  };

  const loadCurrentMasterRecord = async () => {
    try {
      const records = await MasterRecord.filter({ record_key: selectedRecordKey });
      setCurrentMasterRecord(records && records.length > 0 ? records[0] : null);
    } catch (error) {
      console.error('Error loading master record:', error);
      setCurrentMasterRecord(null);
    }
  };

  const loadRecordDetails = async () => {
    setRecordLoading(true);
    try {
      const response = await runEndpoint('cortex.get_record_field_details', {
        dataSourceId: parseInt(selectedDataSourceId),
        datePath: selectedDatePath,
        recordKey: selectedRecordKey
      });

      if (response?.error) {
        toast.error(response.error);
        setRecordFields(null);
      } else {
        setRecordFields(response);
      }
    } catch (error) {
      console.error('Error loading record details:', error);
      toast.error('Failed to load record details');
      setRecordFields(null);
    } finally {
      setRecordLoading(false);
    }
  };

  // --- Load reference list values for this data source's mapped fields ---

  const loadRefListValues = async () => {
    try {
      const mappings = await SourceFieldMappingView.filter({ source_id: selectedDataSourceId });
      const refListFields = (mappings || []).filter(m => m.reference_list_id);
      if (refListFields.length === 0) {
        setRefListValues({});
        return;
      }

      // Deduplicate list IDs
      const listIds = [...new Set(refListFields.map(m => m.reference_list_id))];
      const listValuesMap = {};

      await Promise.all(listIds.map(async (listId) => {
        const values = await ReferenceValueView.filter({ list_id: listId }, 'canonical_value');
        listValuesMap[listId] = (values || []).map(v => v.canonical_value);
      }));

      // Map canonical_name → values[]
      const result = {};
      for (const m of refListFields) {
        result[m.element_canonical_name] = listValuesMap[m.reference_list_id] || [];
      }
      setRefListValues(result);
    } catch (error) {
      console.error('Error loading reference list values:', error);
      setRefListValues({});
    }
  };

  useEffect(() => {
    if (selectedDataSourceId) {
      loadRefListValues();
    } else {
      setRefListValues({});
    }
  }, [selectedDataSourceId]);

  // --- Master field_values helpers ---

  const masterFieldValues = useMemo(() => {
    if (!currentMasterRecord?.field_values) return {};
    return typeof currentMasterRecord.field_values === 'string'
      ? JSON.parse(currentMasterRecord.field_values)
      : currentMasterRecord.field_values;
  }, [currentMasterRecord]);

  // --- Diff logic ---

  const fieldDiffs = useMemo(() => {
    if (!recordFields?.fields || !selectedRecordKey) return {};
    if (!Object.keys(masterFieldValues).length) return {};

    const diffs = {};
    for (const field of recordFields.fields) {
      if (field.category === 'sourceFields') continue;
      const masterEntry = masterFieldValues[field.fieldName];
      if (masterEntry && masterEntry.value !== (field.processedValue ?? null)) {
        diffs[field.fieldName] = masterEntry;
      }
    }
    return diffs;
  }, [recordFields, masterFieldValues, selectedRecordKey]);

  const diffCount = Object.keys(fieldDiffs).length;

  // --- Field editing ---

  const toggleFieldEditing = (fieldName) => {
    setFieldOverrides(prev => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        editing: !prev[fieldName]?.editing,
      }
    }));
  };

  const updateFieldOverride = (fieldName, editValue) => {
    setFieldOverrides(prev => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        editValue,
        validationResult: null, // Reset validation when value changes
      }
    }));
  };

  const validateField = async (fieldName) => {
    const override = fieldOverrides[fieldName];
    const editValue = override?.editValue;
    if (editValue == null || editValue === '') return;

    setFieldOverrides(prev => ({
      ...prev,
      [fieldName]: { ...prev[fieldName], validating: true }
    }));

    try {
      const result = await runEndpoint('cortex.process_single_field', {
        dataSourceId: parseInt(selectedDataSourceId),
        fieldName,
        rawValue: editValue,
      });

      setFieldOverrides(prev => ({
        ...prev,
        [fieldName]: {
          ...prev[fieldName],
          validating: false,
          validationResult: result,
        }
      }));
    } catch (error) {
      console.error('Validation error:', error);
      setFieldOverrides(prev => ({
        ...prev,
        [fieldName]: {
          ...prev[fieldName],
          validating: false,
          validationResult: { isValid: false, message: 'Validation request failed' },
        }
      }));
    }
  };

  // --- Accept single field to master ---

  const handleAcceptField = async (fieldName) => {
    const override = fieldOverrides[fieldName];
    if (!override?.validationResult?.isValid) return;

    try {
      const fieldEntry = {
        value: override.validationResult.processedValue ?? override.editValue,
        data_source_id: parseInt(selectedDataSourceId),
        source_date_path: selectedDatePath,
      };

      const mergedFieldValues = {
        ...masterFieldValues,
        [fieldName]: fieldEntry,
      };

      const payload = {
        record_key: String(selectedRecordKey),
        field_values: mergedFieldValues,
        record_scores: recordFields?.recordScores || null,
        notes: reviewerNotes || null,
      };

      let result;
      if (currentMasterRecord) {
        result = await MasterRecord.update(currentMasterRecord.id, payload);
      } else {
        result = await MasterRecord.create(payload);
      }

      setCurrentMasterRecord({
        ...payload,
        id: result?.id || currentMasterRecord?.id,
        field_values: mergedFieldValues,
      });

      // Mark field as accepted in overrides
      setFieldOverrides(prev => ({
        ...prev,
        [fieldName]: { ...prev[fieldName], accepted: true }
      }));

      toast.success(`Field "${fieldName}" saved to master`);
    } catch (error) {
      console.error('Error accepting field:', error);
      toast.error(`Failed to save field "${fieldName}"`);
    }
  };

  // --- One-click approve source value into master ---

  const approveSourceValue = async (fieldName, sourceValue) => {
    try {
      const fieldEntry = {
        value: sourceValue,
        data_source_id: parseInt(selectedDataSourceId),
        source_date_path: selectedDatePath,
      };

      const mergedFieldValues = {
        ...masterFieldValues,
        [fieldName]: fieldEntry,
      };

      const payload = {
        record_key: String(selectedRecordKey),
        field_values: mergedFieldValues,
        record_scores: recordFields?.recordScores || null,
        notes: reviewerNotes || null,
      };

      let result;
      if (currentMasterRecord) {
        result = await MasterRecord.update(currentMasterRecord.id, payload);
      } else {
        result = await MasterRecord.create(payload);
      }

      setCurrentMasterRecord({
        ...payload,
        id: result?.id || currentMasterRecord?.id,
        field_values: mergedFieldValues,
      });

      setFieldOverrides(prev => ({
        ...prev,
        [fieldName]: { ...prev[fieldName], sourceApproved: true, accepted: true }
      }));

      toast.success(`Field "${fieldName}" saved to master`);
    } catch (error) {
      console.error('Approve error:', error);
      toast.error(`Failed to save field "${fieldName}"`);
    }
  };;;

  // --- Apply fix to all identical issues ---

  const handleApplyToSimilar = (fieldName) => {
    const field = groupedFields.mapped.find(f => f.fieldName === fieldName);
    if (!field) return;
    const override = fieldOverrides[fieldName];
    if (!override?.editValue) return;

    const key = getIssueKey(field);
    const similarFields = issueGroups[key] || [];

    for (const fn of similarFields) {
      if (fn === fieldName) continue; // skip the source field
      setFieldOverrides(prev => ({
        ...prev,
        [fn]: {
          ...prev[fn],
          editValue: override.editValue,
          validationResult: null,
        }
      }));
    }

    toast.success(`Applied to ${similarFields.length - 1} similar field${similarFields.length - 1 !== 1 ? 's' : ''}`);
  };

  // --- Actions ---

  const handleApproveToMaster = async () => {
    if (!selectedRecordKey || !recordFields) return;
    setSaving(true);

    try {
      const fields = recordFields.fields || [];
      const fieldValues = {};

      for (const f of fields) {
        if (f.category === 'sourceFields') continue;

        if (f.category === 'validDataElements') {
          // Auto-promote valid fields
          fieldValues[f.fieldName] = {
            value: f.processedValue ?? null,
            data_source_id: parseInt(selectedDataSourceId),
            source_date_path: selectedDatePath,
          };
        } else if (f.category === 'invalidDataElements') {
          // Only promote if user edited + validated successfully
          const override = fieldOverrides[f.fieldName];
          if (override?.validationResult?.isValid) {
            fieldValues[f.fieldName] = {
              value: override.validationResult.processedValue ?? override.editValue,
              data_source_id: parseInt(selectedDataSourceId),
              source_date_path: selectedDatePath,
            };
          }
        }
      }

      // Merge with existing master (keep fields from other sources)
      const mergedFieldValues = {
        ...masterFieldValues,
        ...fieldValues,
      };

      const payload = {
        record_key: String(selectedRecordKey),
        field_values: mergedFieldValues,
        record_scores: recordFields.recordScores || null,
        notes: reviewerNotes || null,
      };

      let result;
      if (currentMasterRecord) {
        result = await MasterRecord.update(currentMasterRecord.id, payload);
      } else {
        result = await MasterRecord.create(payload);
      }

      // Update local master record
      setCurrentMasterRecord({
        ...payload,
        id: result?.id || currentMasterRecord?.id,
        field_values: mergedFieldValues,
      });

      toast.success(currentMasterRecord ? 'Master record updated' : 'Record approved to master');

      // Auto-advance to next record
      advanceToNextRecord();
    } catch (error) {
      console.error('Error approving to master:', error);
      toast.error('Failed to approve record');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    advanceToNextRecord();
  };

  const advanceToNextRecord = () => {
    const currentIdx = queueRecords.findIndex(r => r.recordKey === selectedRecordKey);
    if (currentIdx + 1 < queueRecords.length) {
      setSelectedRecordKey(queueRecords[currentIdx + 1].recordKey);
    } else {
      toast.info('No more records in the queue');
    }
  };

  const handleNextRecord = () => {
    advanceToNextRecord();
  };

  // --- Evidence panel ---

  const handleFieldClick = async (canonicalName) => {
    setSelectedFieldName(canonicalName);
    setShowMasterView(false);
    setSelectedMasterField(null);
    setElementInfoLoading(true);
    setElementInfo(null);

    try {
      const elements = await DataElement.filter({ canonical_name: canonicalName });
      if (elements && elements.length > 0) {
        const element = elements[0];
        const versions = await ElementVersion.filter({ element_id: element.id });
        const currentVersion = versions.find(v => v.version_number === element.current_version_number) || versions[0];

        let processingRules = [];
        let referenceList = null;
        if (currentVersion) {
          try {
            const ruleLinks = await ElementVersionProcessingRule.filter({
              element_version_id: currentVersion.id
            }, 'execution_order');

            if (ruleLinks && ruleLinks.length > 0) {
              const rules = await Promise.all(
                ruleLinks.map(rl => ProcessingRule.filter({ id: rl.processing_rule_id }))
              );
              processingRules = rules.flat().filter(Boolean);
            }

            if (currentVersion.reference_list_id) {
              const refLists = await ReferenceList.filter({ id: currentVersion.reference_list_id });
              referenceList = refLists && refLists.length > 0 ? refLists[0] : null;
            }
          } catch (e) {
            console.log('Could not fetch processing rules or reference list:', e);
          }
        }

        setElementInfo({
          canonicalName,
          element,
          currentVersion,
          processingRules,
          referenceList,
        });
      } else {
        setElementInfo({ canonicalName, notFound: true });
      }
    } catch (error) {
      console.error('Error loading element info:', error);
      setElementInfo(null);
    } finally {
      setElementInfoLoading(false);
    }
  };

  // --- Filtered queue ---

  const filteredQueue = useMemo(() => {
    if (!queueSearch) return queueRecords;
    const q = queueSearch.toLowerCase();
    return queueRecords.filter(r => String(r.recordKey).toLowerCase().includes(q));
  }, [queueRecords, queueSearch]);

  // --- Field grouping ---
  const groupedFields = useMemo(() => {
    if (!recordFields?.fields) return { mapped: [], source: [] };
    const fields = recordFields.fields;
    return {
      mapped: fields.filter(f => f.category !== 'sourceFields'),
      source: fields.filter(f => f.category === 'sourceFields'),
    };
  }, [recordFields]);

  // Visible fields based on display mode
  const visibleFields = useMemo(() => {
    let fields;
    if (fieldDisplayMode === 'all') {
      fields = groupedFields.mapped;
    } else {
      // 'diffs' mode: show fields that differ from master, or not in master at all
      if (!Object.keys(masterFieldValues).length) {
        fields = groupedFields.mapped;
      } else {
        fields = groupedFields.mapped.filter(f => {
          const masterEntry = masterFieldValues[f.fieldName];
          if (!masterEntry) return true;
          return masterEntry.value !== (f.processedValue ?? null);
        });
      }
    }
    // Sort: valid fields first, invalid fields below
    return [...fields].sort((a, b) => {
      const aInvalid = a.category === 'invalidDataElements' ? 1 : 0;
      const bInvalid = b.category === 'invalidDataElements' ? 1 : 0;
      return aInvalid - bInvalid;
    });
  }, [groupedFields.mapped, masterFieldValues, fieldDisplayMode]);

  // Group identical issues: { issueKey: [fieldName, ...] }
  const issueGroups = useMemo(() => {
    const groups = {};
    for (const f of groupedFields.mapped) {
      if (isSkippableIssue(f)) continue;
      const key = getIssueKey(f);
      if (!groups[key]) groups[key] = [];
      groups[key].push(f.fieldName);
    }
    return groups;
  }, [groupedFields.mapped]);

  // Count of corrected fields (validated successfully)
  const correctedFieldCount = useMemo(() => {
    return Object.values(fieldOverrides).filter(o => o.validationResult?.isValid).length;
  }, [fieldOverrides]);

  // --- Render ---

  return (
    <div data-tour="page-data-review" className="pt-[30px] pb-5 px-4 md:px-[60px] bg-page-bg flex flex-col gap-5 min-h-full md:h-full">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <h1 className="text-2xl md:text-[32px] font-medium leading-tight text-page-title break-words min-w-0">Data Review</h1>
        <div className="flex flex-wrap items-center gap-2">
          {/* Data Source selector */}
          <FilterSelect
            value={selectedDataSourceId ? String(selectedDataSourceId) : ''}
            onValueChange={(v) => setSelectedDataSourceId(parseInt(v))}
            placeholder="Select data source..."
            className="w-52"
          >
            {dataSources.map(ds => (
              <FilterSelectItem key={ds.id} value={String(ds.id)}>{ds.source_name}</FilterSelectItem>
            ))}
          </FilterSelect>

          {/* Date selector */}
          {availableDates.length > 0 && (
            <FilterSelect
              value={selectedDatePath || ''}
              onValueChange={(v) => setSelectedDatePath(v)}
              placeholder="Select date..."
              className="w-72"
            >
              {availableDates.map(entry => (
                <FilterSelectItem key={entry.datePath} value={entry.datePath}>
                  {entry.datePath.replace(/year=(\d+)\/month=(\d+)\/day=(\d+)/, (_, y, m, d) => `${y}/${m.padStart(2,'0')}/${d.padStart(2,'0')}`)} ({entry.fileCount} file{entry.fileCount !== 1 ? 's' : ''})
                </FilterSelectItem>
              ))}
            </FilterSelect>
          )}
          {datesLoading && (
            <span className="text-sm text-page-muted self-center">Loading dates...</span>
          )}
        </div>
      </div>

      {/* Summary stats */}
      {actSummary && (
        <div className="flex flex-wrap items-center gap-3 text-sm text-page-muted">
          <span><strong className="text-page-text">{queueTotal.toLocaleString()}</strong> records</span>
          {actSummary.avgTotalActScore != null && (
            <>
              <span className="text-divider">|</span>
              <span>Avg ACT: <strong className="text-page-text">{actSummary.avgTotalActScore.toFixed(2)}</strong>/8</span>
            </>
          )}
        </div>
      )}

      {/* Body: three-panel layout */}
      {!selectedDataSourceId || !selectedDatePath ? (
        <div className="flex-1 min-h-[400px] flex items-center justify-center">
          <div className="text-center flex flex-col items-center gap-3">
            <ClipboardCheck size={48} color="#D0DCEA" />
            <p className="text-base text-page-muted">Select a data source to begin review</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col md:flex-row md:gap-5 md:flex-1 md:min-h-0">
          {/* Left panel - Work Queue (transparent) */}
          <div className="md:w-[220px] md:shrink-0 md:h-full flex flex-col -mx-4 md:mx-0">
            <div className="px-4 md:px-3 py-2">
              <div className="flex items-center h-9 bg-card-bg rounded-[18px] px-3 gap-2">
                <Search size={14} color="#377AD6" className="shrink-0" />
                <input
                  placeholder="Search records..."
                  value={queueSearch}
                  onChange={(e) => setQueueSearch(e.target.value)}
                  className="flex-1 bg-transparent text-sm outline-none border-0 text-page-text"
                />
              </div>
            </div>

            <div className="md:flex-1 md:min-h-0 md:overflow-y-auto overflow-x-auto md:overflow-x-visible">
              {queueLoading ? (
                <div className="p-3 flex md:flex-col gap-2">
                  {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-14 w-[180px] md:w-auto shrink-0 md:shrink rounded-[5px]" />)}
                </div>
              ) : dateNotProcessed ? (
                <div className="p-5 flex flex-col items-center gap-3">
                  <AlertTriangle size={28} color="rgba(233,171,48,0.9)" />
                  <p className="text-sm text-center text-page-muted">This date has not been processed yet</p>
                  <button
                    onClick={handleRunProcessing}
                    disabled={processing}
                    className="flex items-center gap-2 h-9 px-4 rounded-[5px] text-sm font-medium text-white border-0 cursor-pointer disabled:opacity-50 bg-nav-active hover:bg-nav-active-dark transition-colors"
                  >
                    {processing ? <><RotateCw size={14} className="animate-spin" />Processing...</> : <><DatabaseZap size={14} />Run Processing</>}
                  </button>
                </div>
              ) : filteredQueue.length === 0 ? (
                <div className="p-5 text-center text-sm text-page-muted">
                  {queueRecords.length === 0 ? 'No records found' : 'No matching records'}
                </div>
              ) : (
                <div className="px-4 md:px-2 py-2 flex md:flex-col gap-2 md:gap-1">
                  {filteredQueue.map(record => {
                    const isSelected = record.recordKey === selectedRecordKey;
                    const cfg = getScoreColor(record.avg_field_score, 8);
                    return (
                      <button
                        key={record.recordKey}
                        onClick={() => setSelectedRecordKey(record.recordKey)}
                        className={`text-left px-3 py-2.5 rounded-[5px] border transition-colors cursor-pointer min-w-[180px] md:min-w-0 md:w-full shrink-0 md:shrink ${isSelected ? 'bg-[var(--nav-active-bg)] border-nav-active' : 'bg-card-bg border-divider'}`}
                      >
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-sm font-medium truncate max-w-[120px] text-page-text">
                            {record.recordKey}
                          </span>
                          <span className="inline-flex items-center h-[18px] rounded-[9px] px-2 text-xs font-medium shrink-0"
                            style={{ background: cfg.bg, color: cfg.color }}>
                            {record.avg_field_score?.toFixed(1)}
                          </span>
                        </div>
                        <div className="text-xs text-page-muted">{record.field_count} fields</div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Queue pagination */}
            {queueTotal > 50 && (
              <div className="px-4 md:px-3 py-3 flex items-center justify-center gap-2 text-xs shrink-0">
                <span className="text-page-muted">Page {queuePage} of {Math.ceil(queueTotal / 50)}</span>
                <button
                  className="h-7 px-3 rounded-[5px] cursor-pointer border-2 border-nav-active text-nav-active bg-card-bg font-medium hover:border-nav-active-hover hover:text-nav-active-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  disabled={queuePage <= 1}
                  onClick={() => setQueuePage(p => p - 1)}
                >Prev</button>
                <button
                  className="h-7 px-3 rounded-[5px] cursor-pointer border-2 border-nav-active text-nav-active bg-card-bg font-medium hover:border-nav-active-hover hover:text-nav-active-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  onClick={() => setQueuePage(p => p + 1)}
                  disabled={queuePage * 50 >= queueTotal}
                >Next</button>
              </div>
            )}
          </div>

          {/* Right block: card with center + right panels (transparent when nothing selected) */}
          <div className={`flex-1 md:min-w-0 rounded-[8px] flex md:h-full md:overflow-hidden ${selectedRecordKey ? 'bg-card-bg' : ''}`}>
          {/* Center panel - Record Detail */}
          <div className="flex-1 min-w-0 md:overflow-y-auto">
            {selectedRecordKey === null ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center flex flex-col items-center gap-2">
                  <ChevronRight size={36} color="#D0DCEA" />
                  <p className="text-sm text-page-muted">Select a record from the queue to review</p>
                </div>
              </div>
            ) : recordLoading ? (
              <div className="p-6 flex flex-col gap-4">
                <Skeleton className="h-20 rounded-[8px]" />
                <Skeleton className="h-48 rounded-[8px]" />
              </div>
            ) : !recordFields ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-sm text-page-muted">No field details available</p>
              </div>
            ) : (
              <div className="p-5 flex flex-col gap-4">
                {/* Record header */}
                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-1">
                    <h2 className="text-[20px] font-medium text-page-title">
                      Record: {selectedRecordKey}
                    </h2>
                    <div className="flex items-center gap-2">
                      {currentMasterRecord ? (
                        <span
                          className="inline-flex items-center gap-1 h-[18px] rounded-[9px] pl-1 pr-2 cursor-pointer bg-[var(--pill-green-strong-bg)]"
                          onClick={() => { setShowMasterView(v => !v); setSelectedFieldName(null); setElementInfo(null); setSelectedMasterField(null); }}
                        >
                          <span className="size-[10px] rounded-full shrink-0 bg-[var(--pill-green-strong-color)]" />
                          <span className="text-xs font-medium text-pill-green">In Master</span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center h-[18px] rounded-[9px] px-2 text-xs font-medium bg-[var(--pill-neutral-bg)] text-[var(--pill-neutral-color)]">New</span>
                      )}
                      {currentMasterRecord && (
                        <span className="text-xs text-page-muted">{Object.keys(masterFieldValues).length} fields in master</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Diff banner */}
                {currentMasterRecord && diffCount === 0 && (
                  <div className="px-4 py-3 rounded-[5px] flex items-center gap-3" style={{ background: 'var(--pill-green-strong-bg)', border: '1px solid var(--pill-green-color)' }}>
                    <CheckCircle2 size={16} color="#4DB300" className="shrink-0" />
                    <span className="text-sm text-pill-green">All source values match master</span>
                  </div>
                )}
                {currentMasterRecord && diffCount > 0 && (
                  <div className="px-4 py-3 rounded-[5px] flex items-center gap-3" style={{ background: 'var(--pill-amber-bg)', border: '1px solid var(--pill-amber-color)' }}>
                    <AlertTriangle size={16} color="rgba(233,171,48,0.9)" className="shrink-0" />
                    <span className="text-sm text-[var(--pill-amber-dark)]">
                      <strong>{diffCount} field{diffCount !== 1 ? 's' : ''}</strong> differ from current master values
                    </span>
                  </div>
                )}

                {/* Record ACT scores */}
                {recordFields.recordScores && (
                  <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                    {[
                      { label: 'Accuracy', value: recordFields.recordScores.record_accuracy_score },
                      { label: 'Completeness', value: recordFields.recordScores.record_completeness_score },
                      { label: 'Timeliness', value: recordFields.recordScores.record_timeliness_score },
                      { label: 'Total ACT', value: recordFields.recordScores.record_total_act_score, highlight: true },
                      { label: 'Fields', value: recordFields.recordScores.field_count },
                    ].map(({ label, value, highlight }) => (
                      <div key={label} className="text-center p-3 rounded-[8px] bg-card-bg border border-divider">
                        <p className="text-xs mb-1 text-page-muted">{label}</p>
                        <p className="text-lg font-medium" style={{ color: highlight ? 'var(--nav-active)' : 'var(--page-text)' }}>{value}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Master-centric field comparison */}
                {groupedFields.mapped.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-medium flex items-center gap-2 text-page-text">
                        <DatabaseZap size={16} color="#377AD6" />
                        Fields ({groupedFields.mapped.length})
                        {correctedFieldCount > 0 && (
                          <span className="inline-flex items-center h-[18px] rounded-[9px] px-2 text-xs font-medium bg-[var(--pill-green-strong-bg)] text-pill-green">
                            {correctedFieldCount} corrected
                          </span>
                        )}
                      </h3>
                      <div className="flex items-center gap-2">
                        {diffCount > 0 && (
                          <span className="inline-flex items-center h-[18px] rounded-[9px] px-2 text-xs font-medium bg-[var(--pill-amber-bg)] text-[var(--pill-amber-dark)]">
                            {diffCount} diffs
                          </span>
                        )}
                        <div className="flex rounded-[5px] overflow-hidden border border-divider text-xs">
                          <button
                            className={`px-3 py-1 cursor-pointer border-0 ${fieldDisplayMode === 'diffs' ? 'bg-[var(--nav-active-bg)] text-nav-active font-medium' : 'bg-card-bg text-page-muted font-normal'}`}
                            onClick={() => setFieldDisplayMode('diffs')}
                          >Diffs Only</button>
                          <button
                            className={`px-3 py-1 cursor-pointer border-0 border-l border-divider ${fieldDisplayMode === 'all' ? 'bg-[var(--nav-active-bg)] text-nav-active font-medium' : 'bg-card-bg text-page-muted font-normal'}`}
                            onClick={() => setFieldDisplayMode('all')}
                          >All Fields</button>
                        </div>
                      </div>
                    </div>

                    {visibleFields.length === 0 && fieldDisplayMode === 'diffs' ? (
                      <div className="px-4 py-6 text-center rounded-[8px]" style={{ background: 'var(--pill-green-strong-bg)', border: '1px solid var(--pill-green-color)' }}>
                        <CheckCircle2 size={24} color="#4DB300" className="mx-auto mb-2" />
                        <p className="text-sm font-medium text-pill-green">No differences from master</p>
                        <p className="text-xs mt-1 text-pill-green opacity-70">
                          All {groupedFields.mapped.length} mapped fields match current master values.
                        </p>
                        <button className="mt-2 text-xs cursor-pointer bg-transparent border-0 underline text-nav-active"
                          onClick={() => setFieldDisplayMode('all')}>
                          Show all fields
                        </button>
                      </div>
                    ) : visibleFields.length > 0 ? (
                      <MasterFieldTable
                        fields={visibleFields}
                        fieldDiffs={fieldDiffs}
                        fieldOverrides={fieldOverrides}
                        masterFieldValues={masterFieldValues}
                        dataSources={dataSources}
                        onFieldClick={handleFieldClick}
                        onEditChange={updateFieldOverride}
                        onValidate={validateField}
                        onAcceptField={handleAcceptField}
                        onApproveSourceValue={approveSourceValue}
                        onApplyToSimilar={handleApplyToSimilar}
                        onToggleEditing={toggleFieldEditing}
                        onMasterClick={(fieldName) => { setSelectedMasterField(fieldName); setSelectedFieldName(null); setElementInfo(null); setShowMasterView(false); }}
                        issueGroups={issueGroups}
                        refListValues={refListValues}
                        selectedFieldName={selectedFieldName}
                      />
                    ) : null}
                  </div>
                )}

                {/* Unmapped source fields */}
                {groupedFields.source.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium mb-2 flex items-center gap-2 text-page-muted">
                      <AlertTriangle size={14} />
                      Unmapped Source Fields ({groupedFields.source.length})
                    </h3>
                    <div className="rounded-[8px] overflow-hidden border border-divider">
                      <div className="flex items-center h-9 px-4 gap-4 bg-table-header">
                        <div className="flex-1 text-sm font-medium text-nav-active">Field</div>
                        <div className="flex-1 text-sm font-medium text-nav-active">Raw Value</div>
                      </div>
                      {groupedFields.source.map((f, i) => (
                        <div key={i} className={`flex items-center h-10 px-4 gap-4 ${i % 2 === 0 ? 'bg-table-stripe' : 'bg-card-bg'}`}>
                          <div className="flex-1 text-sm font-medium truncate text-page-text">{f.fieldName}</div>
                          <div className="flex-1 text-sm truncate text-page-muted">
                            {f.rawValue || <span className="italic">empty</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-page-text">Notes</label>
                  <Textarea
                    placeholder="Add notes about this record..."
                    value={reviewerNotes}
                    onChange={(e) => setReviewerNotes(e.target.value)}
                    className="h-20 resize-none rounded-[5px] border text-sm border-[var(--input-border)]"
                  />
                </div>

                {/* Action bar */}
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={handleApproveToMaster}
                    disabled={saving}
                    className="flex items-center gap-2 h-9 px-4 rounded-[5px] text-sm font-medium text-white border-0 cursor-pointer disabled:opacity-50 bg-[var(--pill-green-strong-color)] hover:bg-nav-active-dark transition-colors"
                  >
                    <DatabaseZap size={20} strokeWidth={2.5} />
                    Save to Master
                  </button>
                  <button
                    onClick={handleSkip}
                    disabled={saving}
                    className="flex items-center gap-2 h-9 px-4 rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg text-sm font-medium cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <SkipForward size={20} strokeWidth={2.5} />
                    Skip
                  </button>
                  <button
                    onClick={handleNextRecord}
                    className="flex items-center gap-1.5 h-9 rounded-[5px] text-sm font-medium cursor-pointer border-0 bg-transparent md:ml-auto text-nav-active hover:text-nav-active-hover transition-colors p-0"
                  >
                    Next Record
                    <ArrowRight size={16} strokeWidth={2.5} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right panel - Evidence, Master View, or Master Field Detail */}
          {(selectedFieldName || showMasterView || selectedMasterField) && (
            <div className="md:w-96 border-l border-divider md:shrink-0 md:overflow-y-auto">
              {selectedMasterField ? (
                (() => {
                  const entry = masterFieldValues[selectedMasterField];
                  const dsName = entry ? (dataSources.find(ds => ds.id === entry.data_source_id)?.source_name || `ds:${entry.data_source_id}`) : null;
                  const override = fieldOverrides[selectedMasterField];
                  const wasCorrected = override?.accepted === true && !override?.sourceApproved;
                  const sourceField = recordFields?.fields?.find(f => f.fieldName === selectedMasterField);
                  return (
                    <>
                      <div className="px-5 py-3 border-b border-divider flex items-center justify-between">
                        <h3 className="text-sm font-medium text-page-text">Master: {selectedMasterField}</h3>
                        <button className="bg-transparent border-0 cursor-pointer p-0 text-page-muted" onClick={() => setSelectedMasterField(null)}>
                          <X size={18} />
                        </button>
                      </div>
                      <div className="p-5 flex flex-col gap-4">
                        {/* Value */}
                        <div className="flex flex-col gap-1">
                          <h4 className="text-xs font-medium uppercase tracking-wide text-page-muted">Value</h4>
                          <div className="text-sm font-medium p-3 rounded-[5px] bg-table-stripe text-page-text">
                            {entry?.value ?? <span className="italic text-page-muted">not in master</span>}
                          </div>
                        </div>

                        {/* Source info */}
                        {entry && (
                          <div className="flex flex-col gap-1">
                            <h4 className="text-xs font-medium uppercase tracking-wide text-page-muted">Source</h4>
                            <div className="p-3 rounded-[5px] flex flex-col gap-0.5 bg-table-stripe">
                              <div className="text-sm text-page-text">{wasCorrected ? 'Reviewer corrected' : dsName}</div>
                              {entry.source_date_path && (
                                <div className="text-xs text-page-muted">
                                  {entry.source_date_path.replace(/year=(\d+)\/month=(\d+)\/day=(\d+)/, (_, y, m, d) => `${y}/${m.padStart(2,'0')}/${d.padStart(2,'0')}`)}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* ACT Scores */}
                        {sourceField && !wasCorrected && (
                          <div className="flex flex-col gap-1">
                            <h4 className="text-xs font-medium uppercase tracking-wide text-page-muted">ACT Scores</h4>
                            <div className="grid grid-cols-3 gap-2">
                              {[
                                { label: 'Accuracy', score: sourceField.accuracy_score, max: 1, reason: sourceField.accuracy_reason },
                                { label: 'Completeness', score: sourceField.completeness_score, max: 3, reason: sourceField.completeness_reason },
                                { label: 'Timeliness', score: sourceField.timeliness_score, max: 4 },
                              ].map(({ label, score, max, reason }) => {
                                const cfg = getScoreColor(score, max);
                                return (
                                  <div key={label} className="p-2 rounded-[5px] text-center bg-table-stripe">
                                    <p className="text-xs mb-1 text-page-muted">{label}</p>
                                    <ScorePill score={score} max={max} reason={reason} />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Source File History (without master value — shown separately above) */}
                        <div>
                          <h4 className="text-xs font-medium text-slate-500 uppercase mb-2">Source File History</h4>
                          <FieldChangeHistory
                            dataSourceId={selectedDataSourceId}
                            fieldName={selectedMasterField}
                            recordKey={String(selectedRecordKey)}
                            masterFieldEntry={null}
                            highlightDatePath={entry?.source_date_path}
                            currentDatePath={selectedDatePath}
                            onDateClick={(datePath) => setSelectedDatePath(datePath)}
                          />
                        </div>
                      </div>
                    </>
                  );
                })()
              ) : showMasterView ? (
                <>
                  <div className="px-5 py-3 border-b border-divider flex items-center justify-between">
                    <h3 className="text-sm font-medium text-page-text">Master Record</h3>
                    <button className="bg-transparent border-0 cursor-pointer p-0 text-page-muted" onClick={() => setShowMasterView(false)}>
                      <X size={18} />
                    </button>
                  </div>
                  <div className="p-5 flex flex-col gap-3">
                    {Object.keys(masterFieldValues).length === 0 ? (
                      <div className="text-sm p-3 rounded-[5px] bg-table-stripe text-page-muted">
                        No fields in master yet.
                      </div>
                    ) : (
                      Object.entries(masterFieldValues).map(([fieldName, entry]) => {
                        const dsName = dataSources.find(ds => ds.id === entry.data_source_id)?.source_name || `ds:${entry.data_source_id}`;
                        const override = fieldOverrides[fieldName];
                        const wasCorrected = override?.accepted === true && !override?.sourceApproved;
                        return (
                          <div key={fieldName} className="rounded-[5px] border border-divider bg-card-bg p-3">
                            <div className="flex items-center justify-between mb-0.5">
                              <span className="text-sm font-medium text-page-text">{fieldName}</span>
                              {wasCorrected && (
                                <span className="inline-flex items-center h-[16px] rounded-[8px] px-2 text-[10px] font-medium bg-[var(--pill-green-strong-bg)] text-pill-green">corrected</span>
                              )}
                            </div>
                            <div className="text-sm text-page-text">
                              {entry.value ?? <span className="italic text-page-muted">empty</span>}
                            </div>
                            <div className="text-[10px] mt-1 text-page-muted">
                              {wasCorrected ? 'Reviewer corrected' : dsName}
                              {entry.source_date_path && ` · ${entry.source_date_path.replace(/year=(\d+)\/month=(\d+)\/day=(\d+)/, (_, y, m, d) => `${y}/${m.padStart(2,'0')}/${d.padStart(2,'0')}`)}`}
                            </div>
                          </div>
                        );
                      })
                    )}
                    {currentMasterRecord?.record_scores && (
                      <div className="flex flex-col gap-1">
                        <h4 className="text-xs font-medium uppercase tracking-wide text-page-muted">Record Scores</h4>
                        <div className="rounded-[5px] border border-divider bg-card-bg p-3">
                          {Object.entries(currentMasterRecord.record_scores).map(([key, val]) => (
                            <div key={key} className="flex items-center justify-between text-sm py-0.5">
                              <span className="text-page-muted">{key}</span>
                              <span className="font-medium text-page-text">{typeof val === 'number' ? val.toFixed(2) : String(val)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {currentMasterRecord?.notes && (
                      <div className="flex flex-col gap-1">
                        <h4 className="text-xs font-medium uppercase tracking-wide text-page-muted">Notes</h4>
                        <div className="text-sm p-3 rounded-[5px] bg-table-stripe text-page-text">
                          {currentMasterRecord.notes}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="px-5 py-3 border-b border-divider flex items-center justify-between">
                    <h3 className="text-sm font-medium text-page-text">Evidence: {selectedFieldName}</h3>
                    <button className="bg-transparent border-0 cursor-pointer p-0 text-page-muted"
                      onClick={() => { setSelectedFieldName(null); setElementInfo(null); }}>
                      <X size={18} />
                    </button>
                  </div>

                  <div className="p-5 flex flex-col gap-4">
                    <FieldScoreBreakdown
                      field={recordFields?.fields?.find(f => f.fieldName === selectedFieldName)}
                    />

                    {elementInfoLoading ? (
                      <div className="flex flex-col gap-2">
                        <Skeleton className="h-8 rounded-[5px]" />
                        <Skeleton className="h-20 rounded-[5px]" />
                      </div>
                    ) : elementInfo?.notFound ? (
                      <div className="text-sm p-3 rounded-[5px] bg-table-stripe text-page-muted">
                        Data element not found in dictionary.
                      </div>
                    ) : elementInfo?.element ? (
                      <div className="flex flex-col gap-3">
                        <Link
                          to={createPageUrl('element-details') + `?id=${elementInfo.element.id}`}
                          target="_blank"
                          className="flex items-center gap-1 text-sm no-underline text-nav-active"
                        >
                          Open Data Element Details
                          <ExternalLink size={12} />
                        </Link>

                        {elementInfo.referenceList && (
                          <div className="flex flex-col gap-1">
                            <h4 className="text-xs font-medium uppercase tracking-wide text-page-muted">Reference List</h4>
                            <div className="border border-divider rounded-[5px] p-3">
                              <Link
                                to={createPageUrl('reference-list-values') + `?id=${elementInfo.referenceList.id}`}
                                target="_blank"
                                className="flex items-center gap-1 text-sm no-underline text-nav-active"
                              >
                                {elementInfo.referenceList.list_name}
                                <ExternalLink size={12} />
                              </Link>
                              {elementInfo.referenceList.total_values && (
                                <p className="text-xs mt-1 text-page-muted">{elementInfo.referenceList.total_values} values</p>
                              )}
                            </div>
                          </div>
                        )}

                        {elementInfo.processingRules?.length > 0 && (
                          <div className="flex flex-col gap-1">
                            <h4 className="text-xs font-medium uppercase tracking-wide text-page-muted">Processing Rules</h4>
                            <div className="border border-divider rounded-[5px] divide-y divide-divider">
                              {elementInfo.processingRules.map((rule, idx) => (
                                <div key={rule.id || idx} className="p-3">
                                  <Link
                                    to={createPageUrl('processing-rules') + `/${rule.id}`}
                                    target="_blank"
                                    className="flex items-center gap-1 text-sm no-underline text-nav-active"
                                  >
                                    {rule.rule_name}
                                    <ExternalLink size={12} />
                                  </Link>
                                  {rule.description && (
                                    <p className="text-xs mt-0.5 text-page-muted">{rule.description}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : null}

                    <div className="flex flex-col gap-1">
                      <h4 className="text-xs font-medium uppercase tracking-wide text-page-muted">Change History</h4>
                      <FieldChangeHistory
                        dataSourceId={selectedDataSourceId}
                        fieldName={selectedFieldName}
                        recordKey={String(selectedRecordKey)}
                        masterFieldEntry={masterFieldValues[selectedFieldName]}
                        currentDatePath={selectedDatePath}
                        onDateClick={(datePath) => setSelectedDatePath(datePath)}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        </div>
      )}
    </div>
  );
}

// --- Sub-components ---

function FieldScoreBreakdown({ field }) {
  if (!field) return null;

  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-xs font-medium uppercase tracking-wide text-page-muted">ACT Score Breakdown</h4>
      <div className="grid grid-cols-2 gap-2">
        {[
          { label: 'Accuracy', score: field.accuracy_score, max: 1, reason: field.accuracy_reason },
          { label: 'Completeness', score: field.completeness_score, max: 3, reason: field.completeness_reason },
          { label: 'Timeliness', score: field.timeliness_score, max: 4 },
          { label: 'Total', score: field.total_act_score, max: 8 },
        ].map(({ label, score, max, reason }) => (
          <div key={label} className="p-2 rounded-[5px] text-center bg-table-stripe">
            <p className="text-xs mb-1 text-page-muted">{label}</p>
            <ScorePill score={score} max={max} reason={reason} />
          </div>
        ))}
      </div>
      {field.rawValue !== field.processedValue && (
        <div className="text-xs p-2 rounded-[5px]" style={{ background: 'var(--nav-active-bg)', border: '1px solid var(--nav-active)' }}>
          <p className="text-page-muted">Raw: <span className="text-page-text">{field.rawValue || 'empty'}</span></p>
          <p className="text-page-muted">Processed: <span className="text-nav-active font-medium">{field.processedValue || 'empty'}</span></p>
        </div>
      )}
    </div>
  );
}

function MasterFieldTable({ fields, fieldDiffs, fieldOverrides, masterFieldValues, dataSources, onFieldClick, onEditChange, onValidate, onAcceptField, onApproveSourceValue, onApplyToSimilar, onToggleEditing, onMasterClick, issueGroups, refListValues, selectedFieldName }) {
  const fieldIssueKey = useMemo(() => {
    const map = {};
    for (const [key, fieldNames] of Object.entries(issueGroups)) {
      for (const fn of fieldNames) map[fn] = key;
    }
    return map;
  }, [issueGroups]);

  return (
    <div className="space-y-3">
      {fields.map((field, idx) => {
        const masterEntry = masterFieldValues[field.fieldName];
        const override = fieldOverrides[field.fieldName] || {};
        const hasDiff = fieldDiffs[field.fieldName] !== undefined;
        const isEditing = override.editing || field.category === 'invalidDataElements';
        const isCorrected = override.accepted === true;
        const isFieldSelected = field.fieldName === selectedFieldName;
        const vr = override.validationResult;
        const refOptions = refListValues[field.fieldName];
        const issueKey = fieldIssueKey[field.fieldName];
        const groupSize = issueKey ? (issueGroups[issueKey]?.length || 0) : 0;
        const masterDsName = masterEntry ? (dataSources.find(ds => ds.id === masterEntry.data_source_id)?.source_name || `ds:${masterEntry.data_source_id}`) : null;

        let borderStyle = { borderColor: '#C8D9EC' };
        if (isCorrected) borderStyle = { borderColor: 'rgba(77,179,0,0.4)' };
        else if (field.category === 'invalidDataElements') borderStyle = { borderColor: 'rgba(222,111,107,0.5)' };
        else if (hasDiff) borderStyle = { borderColor: 'rgba(233,171,48,0.5)' };

        return (
          <div key={idx} className="border rounded-[8px] p-3"
            style={{ ...borderStyle, outline: isFieldSelected ? '1px solid var(--nav-active)' : 'none' }}>
            {/* Field name row */}
            <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-2 mb-2">
              <div className="flex items-center gap-2 min-w-0">
                <button
                  onClick={() => onFieldClick(field.fieldName)}
                  className="text-sm font-medium flex items-center gap-1 cursor-pointer bg-transparent border-0 p-0 text-nav-active"
                >
                  {field.fieldName}
                  <Info size={12} />
                </button>
                {field.sourceFieldName && field.sourceFieldName !== field.fieldName && (
                  <span className="text-xs text-page-muted">({field.sourceFieldName})</span>
                )}
                {groupSize > 1 && (
                  <span className="inline-flex items-center h-[16px] rounded-[8px] px-2 text-[10px] font-medium bg-[var(--pill-neutral-bg)] text-[var(--pill-neutral-color)]">
                    {groupSize} similar
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <ScoreCell label="A" score={field.accuracy_score} max={1} reason={field.accuracy_reason} greyed={isCorrected && !override.sourceApproved} />
                <ScoreCell label="C" score={field.completeness_score} max={3} reason={field.completeness_reason} greyed={isCorrected && !override.sourceApproved} />
                <ScoreCell label="T" score={field.timeliness_score} max={4} greyed={isCorrected && !override.sourceApproved} />
              </div>
            </div>

            {/* Source → Master blocks */}
            <div className="flex items-stretch gap-2">
              {/* Source value block */}
              <div
                className="flex-1 rounded-[5px] p-2 cursor-pointer transition-all bg-card-bg border border-divider"
                onClick={() => onFieldClick(field.fieldName)}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--nav-active)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--divider)'}
              >
                <div className="text-[10px] uppercase tracking-wide mb-0.5 text-page-muted">Source</div>
                <div className="text-sm text-page-text">
                  {field.processedValue || field.rawValue || <span className="italic text-page-muted">empty</span>}
                </div>
                {field.rawValue !== field.processedValue && (
                  <div className="text-[10px] mt-0.5 text-page-muted">raw: {field.rawValue}</div>
                )}
              </div>

              {/* Arrow */}
              <div className="flex items-center">
                <button
                  className="text-lg font-bold transition-all border-0 bg-transparent p-0"
                  style={(() => {
                    const sourceVal = field.processedValue ?? field.rawValue ?? '';
                    const masterVal = masterEntry?.value ?? null;
                    const valuesMatch = masterEntry && String(masterVal) === String(sourceVal);
                    const canApprove = field.category !== 'invalidDataElements' && !valuesMatch && !override.validating;
                    if (!canApprove) return { color: 'var(--divider)', cursor: 'not-allowed' };
                    return { color: 'var(--pill-green-strong-color)', cursor: 'pointer' };
                  })()}
                  onClick={() => onApproveSourceValue(field.fieldName, field.processedValue ?? field.rawValue ?? '')}
                  disabled={(() => {
                    const sourceVal = field.processedValue ?? field.rawValue ?? '';
                    const masterVal = masterEntry?.value ?? null;
                    const valuesMatch = masterEntry && String(masterVal) === String(sourceVal);
                    return field.category === 'invalidDataElements' || valuesMatch || override.validating;
                  })()}
                  title={field.category === 'invalidDataElements' ? 'Source value is invalid' : 'Approve source value to master'}
                >→</button>
              </div>

              {/* Master value block */}
              <div
                className="flex-1 rounded-[5px] p-2 transition-all"
                style={{
                  background: hasDiff ? 'var(--pill-amber-faint-bg)' : 'var(--card-bg)',
                  border: `1px ${masterEntry ? 'solid' : 'dashed'} ${hasDiff ? 'var(--pill-amber-color)' : 'var(--divider)'}`,
                  cursor: masterEntry ? 'pointer' : 'default',
                }}
                onClick={() => masterEntry && onMasterClick(field.fieldName)}
                onMouseEnter={e => { if (masterEntry) e.currentTarget.style.borderColor = 'var(--nav-active)'; }}
                onMouseLeave={e => { if (masterEntry) e.currentTarget.style.borderColor = hasDiff ? 'var(--pill-amber-color)' : 'var(--divider)'; }}
              >
                <div className="text-[10px] uppercase tracking-wide mb-0.5 text-page-muted">Master</div>
                {masterEntry ? (
                  <>
                    <div className="text-sm font-medium" style={{ color: hasDiff ? 'var(--pill-amber-dark)' : 'var(--page-text)' }}>
                      {masterEntry.value ?? <span className="italic text-page-muted">empty</span>}
                    </div>
                    <div className="text-[10px] mt-0.5 text-page-muted">{isCorrected && !override.sourceApproved ? 'Reviewer corrected' : masterDsName}</div>
                  </>
                ) : (
                  <div className="text-xs italic text-page-muted">not in master</div>
                )}
              </div>
            </div>

            {/* Corrected value / Actions row */}
            {(isEditing || isCorrected) && (
              <div className="mt-2 flex items-start gap-2">
                {isEditing && !isCorrected ? (
                  <>
                    <div className="flex-1">
                      {refOptions && refOptions.length > 0 ? (
                        <ComboboxInput
                          value={override.editValue ?? ''}
                          onChange={(val) => onEditChange(field.fieldName, val)}
                          options={refOptions}
                          placeholder="Select or type value..."
                        />
                      ) : (
                        <Input
                          value={override.editValue ?? ''}
                          onChange={(e) => onEditChange(field.fieldName, e.target.value)}
                          placeholder="Enter corrected value..."
                          className="h-7 text-xs"
                        />
                      )}
                      {vr && (
                        <div className="text-xs mt-1 flex items-center gap-1" style={{ color: vr.isValid ? 'var(--pill-green-strong-color)' : 'var(--error-text)' }}>
                          {vr.isValid ? (
                            <><Check size={11} />Valid{vr.processedValue !== override.editValue ? ` → ${vr.processedValue}` : ''}</>
                          ) : (
                            vr.accuracy_reason || vr.message || 'Invalid'
                          )}
                        </div>
                      )}
                      {override.editValue && groupSize > 1 && (
                        <button
                          className="h-6 px-2 text-[10px] mt-1 cursor-pointer bg-transparent border-0 underline text-nav-active"
                          onClick={() => onApplyToSimilar(field.fieldName)}
                        >
                          Apply to {groupSize - 1} similar
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-1 pt-0.5">
                      <button
                        className="h-7 px-2 text-xs rounded-[5px] border cursor-pointer bg-card-bg disabled:opacity-40 border-[var(--input-border)] text-page-text"
                        disabled={!override.editValue || override.validating}
                        onClick={() => onValidate(field.fieldName)}
                      >
                        {override.validating ? <RotateCw size={11} className="animate-spin" /> : 'Check'}
                      </button>
                      {vr?.isValid && !override.accepted && (
                        <button
                          className="h-7 px-2 text-xs rounded-[5px] border-0 cursor-pointer text-white bg-[var(--pill-green-strong-color)]"
                          onClick={() => onAcceptField(field.fieldName)}
                        >Accept</button>
                      )}
                    </div>
                  </>
                ) : isCorrected ? (
                  <span className="inline-flex items-center gap-1 h-[18px] rounded-[9px] px-2 text-xs font-medium bg-[var(--pill-green-strong-bg)] text-pill-green">
                    <Check size={10} />Saved
                  </span>
                ) : null}
              </div>
            )}

            {/* Edit button */}
            {!isEditing && !isCorrected && (
              <div className="mt-1.5">
                <button
                  className="h-6 px-2 text-xs cursor-pointer bg-transparent border-0 text-page-muted"
                  onClick={() => onToggleEditing(field.fieldName)}
                >Edit</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ComboboxInput({ value, onChange, options, placeholder }) {
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (inputRef.current?.contains(e.target)) return;
      if (dropdownRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const handleScroll = () => setOpen(false);
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
    }
  }, [open, value]);

  const filtered = useMemo(() => {
    const term = (value || '').toLowerCase();
    if (!term) return options.slice(0, 50);
    return options.filter(o => o.toLowerCase().includes(term)).slice(0, 50);
  }, [options, value]);

  const dropdown = open && filtered.length > 0
    ? createPortal(
        <div
          ref={dropdownRef}
          className="fixed max-h-48 overflow-y-auto rounded-[8px] border border-divider bg-card-bg shadow-[0_4px_24px_0_var(--shadow-blue)] py-1"
          style={{ top: pos.top, left: pos.left, width: pos.width, zIndex: 9999 }}
        >
          {filtered.map((opt) => (
            <div
              key={opt}
              className={`px-3 h-8 flex items-center text-xs cursor-pointer ${opt === value ? 'font-medium' : ''}`}
              style={{ background: opt === value ? 'var(--table-stripe)' : 'transparent', color: 'var(--page-text)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--table-stripe)'}
              onMouseLeave={e => e.currentTarget.style.background = opt === value ? 'var(--table-stripe)' : 'transparent'}
              onMouseDown={(e) => { e.preventDefault(); onChange(opt); setOpen(false); }}
            >
              {opt}
            </div>
          ))}
        </div>,
        document.body
      )
    : null;

  return (
    <div>
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className="h-7 text-xs"
      />
      {dropdown}
    </div>
  );
}

function ScoreCell({ label, score, max, reason, greyed }) {
  const tooltipText = greyed
    ? `${reason ? reason + ' | ' : ''}Score from original processing — field corrected`
    : reason;
  return (
    <ScorePill
      score={score} max={max}
      label={label}
      reason={tooltipText}
      greyed={greyed}
    />
  );
}
